import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiUser, FiShield, FiMapPin, FiShoppingBag, FiLink, FiBriefcase, FiKey } from 'react-icons/fi';
import Layout from '../../../components/Layout';
import api from '../../../api';

// Tabs split into separate files
import ProfileTab from '../tabs/ProfileTab';
import SecurityTab from '../tabs/SecurityTab';
import AddressTab from '../tabs/AddressTab';
import OrdersTab from '../tabs/OrdersTab';
import OAuth2Tab from '../tabs/OAuth2Tab'; // 👈 NEW
import BusinessTab from '../tabs/BusinessTab';
import { mediaUrl } from '../../../utils/media';

export default function UserView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const preloaded = location.state?.user || null;

  const [user, setUser] = useState(preloaded);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLd] = useState(!preloaded);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [avatarBroken, setAvatarBroken] = useState(false);

  // ---- fetch user (state → /admin/users/:id → /admin/users fallback) ----
  useEffect(() => {
    if (preloaded) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/', { replace: true });
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };

    (async () => {
      setLd(true);
      try {
        const res = await api.get(`/admin/users/${id}`, { headers });
        setUser(res.data.user);
      } catch (err) {
        const msg = err?.response?.data?.message || '';
        const status = err?.response?.status;
        if (status === 404 || /route not found/i.test(msg)) {
          try {
            const list = await api.get('/admin/users', { headers });
            const found = (list.data.users || []).find(u => String(u._id) === String(id));
            if (found) setUser(found);
            else setError('User not found.');
          } catch (e2) {
            setError(e2?.response?.data?.message || 'Could not load user.');
          }
        } else {
          setError(msg || 'Could not load user.');
        }
      } finally {
        setLd(false);
      }
    })();
  }, [id, navigate, preloaded]);

  // ---- avatar and initials ----
  const avatarSrcRaw =
    user?.verifiedPortraitPath ||
    user?.profile?.avatarUrl ||
    user?.selfieImagePath ||
    user?.docFaceImagePath ||
    user?.avatarUrl ||
    user?.photoUrl ||
    user?.profileImage ||
    null;

  const avatarSrc = useMemo(() => (avatarSrcRaw ? mediaUrl(avatarSrcRaw) : null), [avatarSrcRaw]);
  const showAvatarImage = Boolean(avatarSrc) && !avatarBroken;

  useEffect(() => {
    setAvatarBroken(false);
  }, [avatarSrc]);

  const initials = useMemo(() => {
    if (!user) return '';
    const a = (user.firstName || '').charAt(0);
    const b = (user.lastName || '').charAt(0);
    return (a + b).toUpperCase();
  }, [user]);

  // ---- upload photo (admin action for this user) ----
  const fileInputRef = useRef(null);
  const onPickFile = () => fileInputRef.current?.click();

  const onUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setUploading(true);
      const form = new FormData();
      form.append('photo', file);

      const res = await api.post(`/admin/users/${id}/photo`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });

      if (res.data?.user) setUser(res.data.user);
      else if (res.data?.url) setUser(u => ({ ...u, profile: { ...(u?.profile || {}), avatarUrl: res.data.url } }));
    } catch (err) {
      alert(err?.response?.data?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onSendReset = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      setResetSending(true);
      setResetMessage('');
      await api.post(
        `/admin/users/${id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResetMessage(`Password reset code sent to ${user.email}.`);
    } catch (err) {
      setResetMessage(err?.response?.data?.message || 'Could not send reset code.');
    } finally {
      setResetSending(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-white">Loading profile…</p>
        </div>
      </Layout>
    );
  }
  if (error || !user) {
    return (
      <Layout>
        <div className="p-6">
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center gap-2 rounded border border-[#f5b221]/18 bg-[#171717] px-3 py-2 text-white transition hover:border-[#f5b221]/30 hover:text-[#f7d88a]"
          >
            <FiArrowLeft /> Back
          </button>
          <p className="mt-6 text-red-500">{error || 'User not found.'}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Back */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center gap-2 rounded border border-[#f5b221]/18 bg-[#171717] px-3 py-2 text-white transition hover:border-[#f5b221]/30 hover:text-[#f7d88a]"
          >
            <FiArrowLeft /> Back to Users
          </button>
        </div>

        {/* Header / hero */}
        <div className="rounded-2xl border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.14),transparent_30%),linear-gradient(180deg,#171717,#090909)] p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center gap-5">
              {showAvatarImage ? (
                <img
                  src={avatarSrc}
                  alt="Profile"
                  className="w-24 h-24 rounded-2xl object-cover ring-2 ring-yellow-400"
                  onError={() => setAvatarBroken(true)}
                />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-yellow-300 to-yellow-600
                                text-black flex items-center justify-center text-3xl font-bold ring-2 ring-yellow-400">
                  {initials || '👤'}
                </div>
              )}
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-white">
                    {user.firstName} {user.lastName}
                  </h2>
                  {user.role && (
                    <span className="rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider bg-[#f5b221] text-black">
                      {String(user.role)}
                    </span>
                  )}
                </div>
                {user.shimbirrId && (
                  <div className="mt-1 text-sm font-medium text-[#f7d88a]">Shimbirr ID: {user.shimbirrId}</div>
                )}
              </div>
            </div>

            {/* Upload button */}
            <div className="flex flex-wrap justify-end gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onUpload}
              />
              <button
                onClick={onSendReset}
                disabled={resetSending}
                className="inline-flex items-center gap-2 rounded border border-[#f5b221]/30 bg-black/40 px-4 py-2 text-[#f7d88a] transition hover:bg-[#f5b221]/10 disabled:opacity-60"
              >
                <FiKey />
                {resetSending ? 'Sending reset…' : 'Reset Password'}
              </button>
              <button
                onClick={onPickFile}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded border border-[#f5b221] bg-[#f5b221] px-4 py-2 text-black transition hover:bg-[#f7d88a] disabled:opacity-60"
              >
                <FiUpload />
                {uploading ? 'Uploading…' : 'Upload New Photo'}
              </button>
            </div>
          </div>

          {resetMessage ? (
            <div className="mt-5 rounded-2xl border border-[#f5b221]/20 bg-black/35 px-4 py-3 text-sm text-[#f7d88a]">
              {resetMessage}
            </div>
          ) : null}

          {/* Tabs */}
          <div className="mt-6 border-b border-[#f5b221]/15">
            <div className="flex flex-wrap gap-2">
              <TabButton active={activeTab === 'profile'}  onClick={() => setActiveTab('profile')}  icon={<FiUser />}        label="Profile" />
              <TabButton active={activeTab === 'business'} onClick={() => setActiveTab('business')} icon={<FiBriefcase />}   label="Business" />
              <TabButton active={activeTab === 'security'} onClick={() => setActiveTab('security')} icon={<FiShield />}      label="Security" />
              <TabButton active={activeTab === 'address'}  onClick={() => setActiveTab('address')}  icon={<FiMapPin />}      label="Address" />
              <TabButton active={activeTab === 'orders'}   onClick={() => setActiveTab('orders')}   icon={<FiShoppingBag />} label="My Orders" />
              <TabButton active={activeTab === 'oauth2'}   onClick={() => setActiveTab('oauth2')}   icon={<FiLink />}        label="OAuth2" /> {/* 👈 NEW */}
            </div>
          </div>
        </div>

        {/* Tab content */}
        {{
          profile:  <ProfileTab  user={user} userId={user._id} />,
          business: <BusinessTab userId={user._id} />,
          security: <SecurityTab user={user} userId={user._id || user.id} />,
          address:  <AddressTab  user={user} />,
          orders:   <OrdersTab   userId={user._id} />,
          oauth2:   <OAuth2Tab   userId={user._id} />,   // 👈 NEW
        }[activeTab]}
      </div>
    </Layout>
  );
}

/* ----------------------------- Local UI bits ----------------------------- */
function TabButton({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-t-xl px-4 py-2 transition
        ${active ? 'bg-[#f5b221] text-black' : 'text-[#9a9a9a] hover:text-[#f7d88a]'}`}
    >
      {icon} {label}
    </button>
  );
}
